/**
 * useLiveUnallocatedBookings
 *
 * Shared access to the live /bookings/unallocated response.
 *
 * Both the sidebar badge and the Unallocated Bookings page need this list, and
 * each consumer used to own its own fetch — so the endpoint was hit twice (and
 * again on every isLive / hasLiveData transition) even on pages that never show
 * unallocated bookings. Requests are de-duplicated per company here: the first
 * consumer fetches, later ones reuse the in-flight promise or the cached list,
 * and a refresh (or the Unallocated Bookings page mounting) refetches once and
 * pushes the new list to every subscriber.
 */
import { useCallback, useEffect, useState } from 'react';
import { bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import type { Booking as ApiBooking } from '@/services/api';
import type { Booking } from '@/types';

// Preserve the null resourceId from the /bookings/unallocated endpoint; the
// shared mapper converts it to 'unallocated' for calendar compatibility.
function mapUnallocatedApiBooking(apiBooking: ApiBooking): Booking {
  const mapped = mapApiBookingToBooking(apiBooking);
  if (apiBooking.resourceId == null) {
    mapped.resourceId = null;
  }
  return mapped;
}

type Subscriber = (bookings: Booking[]) => void;

const cache = new Map<string, Booking[]>();
const inFlight = new Map<string, Promise<Booking[]>>();
const subscribers = new Map<string, Set<Subscriber>>();

function publish(companyId: string, bookings: Booking[]) {
  cache.set(companyId, bookings);
  subscribers.get(companyId)?.forEach(notify => notify(bookings));
}

function load(companyId: string, force: boolean): Promise<Booking[]> {
  if (!force) {
    const cached = cache.get(companyId);
    if (cached) return Promise.resolve(cached);
    const pending = inFlight.get(companyId);
    if (pending) return pending;
  }

  const request = bookingsApi
    .getUnallocated(false, companyId)
    .then(apiBookings => {
      const bookings = apiBookings.map(mapUnallocatedApiBooking);
      publish(companyId, bookings);
      return bookings;
    })
    .finally(() => {
      if (inFlight.get(companyId) === request) inFlight.delete(companyId);
    });

  inFlight.set(companyId, request);
  return request;
}

/** Test-only: drop the shared cache so each test starts from a clean slate. */
export function resetLiveUnallocatedBookingsCache() {
  cache.clear();
  inFlight.clear();
}

export interface LiveUnallocatedBookingsOptions {
  /** Only fetch while true — e.g. live mode with a company selected. */
  enabled: boolean;
  /**
   * Refetch once when the consumer mounts, so the page that owns this data
   * shows a fresh list instead of whatever the sidebar badge last cached.
   */
  refreshOnMount?: boolean;
}

export function useLiveUnallocatedBookings(
  companyId: string | undefined,
  { enabled, refreshOnMount = false }: LiveUnallocatedBookingsOptions,
) {
  const [bookings, setBookings] = useState<Booking[]>(
    () => (companyId && cache.get(companyId)) || [],
  );

  useEffect(() => {
    if (!enabled || !companyId) {
      setBookings([]);
      return;
    }

    let cancelled = false;
    const notify: Subscriber = next => {
      if (!cancelled) setBookings(next);
    };

    const forCompany = subscribers.get(companyId) ?? new Set<Subscriber>();
    forCompany.add(notify);
    subscribers.set(companyId, forCompany);

    const cached = cache.get(companyId);
    if (cached) setBookings(cached);

    load(companyId, refreshOnMount).catch(() => {
      if (!cancelled && !cached) setBookings([]);
    });

    return () => {
      cancelled = true;
      forCompany.delete(notify);
      if (forCompany.size === 0) subscribers.delete(companyId);
    };
    // refreshOnMount is a fixed per-consumer flag, not a reactive input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, enabled]);

  /** Force a refetch, resolving once every subscriber has the new list. */
  const refresh = useCallback(async () => {
    if (!enabled || !companyId) return [];
    return load(companyId, true);
  }, [companyId, enabled]);

  return { bookings, refresh };
}
